import type { AspectRatio } from './aspect';
import type { ImageModelId } from './models';
import type { ArchitecturalAnalysis, ColorParam, LandscapingConfig } from './types';

/** Versioned REST prefix shared by the API router and the web client. */
export const API_PREFIX = '/api/v1';

/** Header carrying the anonymous per-browser id that scopes generation history. */
export const CLIENT_ID_HEADER = 'x-client-id';

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

/** A persisted makeover render, as returned by the API. */
export interface Generation {
  id: string;
  createdAt: string; // ISO 8601
  model: string;
  modelDisplayName: string;
  originalImageUrl: string;
  renderImageUrl: string;
  aspectRatio: string;
  wall: ColorParam;
  border: ColorParam;
  highlight: ColorParam;
  masterPrompt: string;
  landscaping: LandscapingConfig | null;
  analysis: ArchitecturalAnalysis | null;
  inputTokens: number;
  outputTokens: number;
  analysisTokens: number;
  totalTokens: number;
  estimatedCost: number;
}

export interface GenerationSummary {
  count: number;
  totalCost: number;
  totalTokens: number;
}

export interface CreateMakeoverRequest {
  /** Source photo as a base64 data URL (png, jpeg, webp, heic or heif). */
  image: string;
  wall: ColorParam;
  border: ColorParam;
  highlight: ColorParam;
  /** Editable directives template; placeholders like [WALL_HEX] are filled server-side. */
  masterPrompt: string;
  landscaping?: LandscapingConfig;
  aspectRatio?: AspectRatio;
  model?: ImageModelId;
}

export interface CreateMakeoverResponse {
  generation: Generation;
  /** Any text the image model returned alongside the render. */
  textOutput: string | null;
}

export interface AnalyzeRequest {
  image: string;
}

export interface AnalyzeResponse {
  analysis: ArchitecturalAnalysis;
  tokens: TokenUsage;
}

export interface ListGenerationsParams {
  from?: string;
  to?: string;
  model?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}

export interface ListGenerationsResponse {
  items: Generation[];
  nextCursor: string | null;
  /** Totals across every item matching the filters, not just this page. */
  summary: GenerationSummary;
}

export interface HealthResponse {
  status: 'ok' | 'degraded';
  database: 'up' | 'down';
  aiConfigured: boolean;
  uptimeSeconds: number;
  timestamp: string;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
    requestId?: string;
  };
}
